import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  DEFAULT_DESCRIPTION,
  absoluteUrl,
} from "../../constants/seo";
import { buildBreadcrumbJsonLd } from "../../Helper/structuredData";

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  const selector = `meta[${attr}="${key}"]`;
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

const SeoHead = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  breadcrumbs,
  jsonLd = [],
  noindex = false,
}) => {
  const canonical = absoluteUrl(path);
  const pageTitle = title?.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const schemas = [
    ...(Array.isArray(jsonLd) ? jsonLd : [jsonLd]),
    breadcrumbs?.length ? buildBreadcrumbJsonLd(breadcrumbs) : null,
  ].filter(Boolean);

  useEffect(() => {
    const applyTags = () => {
      document.title = pageTitle;
      upsertMeta("name", "description", description);
      upsertMeta(
        "name",
        "robots",
        noindex
          ? "noindex, nofollow"
          : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      );
      upsertCanonical(canonical);
      upsertMeta("property", "og:type", "website");
      upsertMeta("property", "og:site_name", SITE_NAME);
      upsertMeta("property", "og:locale", "en_IN");
      upsertMeta("property", "og:title", pageTitle);
      upsertMeta("property", "og:description", description);
      upsertMeta("property", "og:url", canonical);
      upsertMeta("property", "og:image", image);
      upsertMeta("name", "twitter:card", "summary_large_image");
      upsertMeta("name", "twitter:title", pageTitle);
      upsertMeta("name", "twitter:description", description);
      upsertMeta("name", "twitter:image", image);

      document.head.querySelectorAll('script[data-seo-page="true"]').forEach((node) => node.remove());
      schemas.forEach((schema) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo-page", "true");
        script.text = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    };

    applyTags();
    const timer = window.setTimeout(applyTags, 0);
    return () => window.clearTimeout(timer);
  }, [pageTitle, description, canonical, image, noindex, JSON.stringify(schemas)]);

  return (
    <Helmet>
      <title>{pageTitle}</title>
    </Helmet>
  );
};

export default SeoHead;
